import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { orderBy } from 'lodash';
import queryString from 'query-string';
import { useLocation, useNavigate } from 'react-router-dom';
import type { FinancialStatementSmall as FinancialStatementVersion } from 'api';
import { notFoundPath } from 'common/config/urls';
import { PRIMARY_FINANCIAL_LABEL } from 'common/constants/financials/financials';
import UnsavedChangesContext from 'context/UnsavedChangesContext';
import { toString } from 'utilities';

const useVersionsFilter = ({ financialVersions }: { financialVersions: FinancialStatementVersion[] }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const { version: versionQueryString } = queryString.parse(location.search);
  const parsedQuery = useMemo(() => queryString.parse(location.search), [location.search]);

  const { action: hasChanges, showDialog } = useContext(UnsavedChangesContext);
  const [selectedVersion, setSelectedVersion] = useState<FinancialStatementVersion | undefined>(undefined);

  const setQueryString = useCallback(
    (paramName: string, newQueryString: string | null | undefined) => {
      parsedQuery[paramName] = newQueryString ?? null;
      navigate({ pathname: location.pathname, search: queryString.stringify(parsedQuery) }, { replace: true });
    },
    [navigate, location.pathname, parsedQuery]
  );

  const handleVersion = useCallback(() => {
    if (financialVersions?.length > 0) {
      const defaultVersion = financialVersions.find(v => !v.versioning);
      const version = versionQueryString || defaultVersion?.id;

      const matchVersion = (v: string | number) =>
        financialVersions.find(item => item.slug === v || item.id?.toString() === v.toString());

      const setVersionQueryString = (v: FinancialStatementVersion) => {
        if (
          !selectedVersion ||
          selectedVersion.id?.toString() !== v.id?.toString() ||
          selectedVersion?.name !== v?.name
        ) {
          setSelectedVersion(v);
          setQueryString('version', v.slug);
        }
      };

      if (version) {
        const matchedVersion = matchVersion(version as string | number);
        if (matchedVersion) {
          setVersionQueryString(matchedVersion);
        } else if (defaultVersion) {
          setVersionQueryString(defaultVersion);
        } else {
          navigate(notFoundPath);
        }
      }
    } else {
      setSelectedVersion(undefined);
    }
  }, [financialVersions, selectedVersion, setQueryString, versionQueryString, navigate]);

  const onVersionChange = useCallback(
    (versionId: string) => {
      const callback = () => {
        const tmpSelectedVersion = financialVersions.find(v => toString(v.id) === versionId);
        setSelectedVersion(tmpSelectedVersion);
        setQueryString('version', tmpSelectedVersion?.slug);
      };

      if (hasChanges) {
        showDialog(callback);
      } else {
        callback();
      }
    },
    [financialVersions, hasChanges, setQueryString, showDialog]
  );

  useEffect(() => {
    if (financialVersions) {
      handleVersion();
    }
  }, [financialVersions, handleVersion]);

  const orderedVersions = useMemo(() => {
    if (!financialVersions?.length) return [];
    return orderBy(financialVersions, 'versioning', 'desc').map(v => ({
      ...v,
      name: !v.versioning ? PRIMARY_FINANCIAL_LABEL : v.name,
    }));
  }, [financialVersions]);

  return { selectedVersion, orderedVersions, onVersionChange };
};

export default useVersionsFilter;
